import type { Capabilities } from "@/features/account/useCurrentUser";

/**
 * Help articles (docs/design/web/WEB_HELP_DESIGN.md): the non-translatable metadata for
 * each article. The prose (title, summary, keywords, body) lives in
 * content/<lng>/<slug>.md. Order here is nav order within each group.
 */

export const HELP_GROUPS = ["start", "use", "spaces", "build", "account", "admin", "faq"] as const;
export type HelpGroupId = (typeof HELP_GROUPS)[number];

/** Gate applied to every article in a group, on top of the article's own. */
export const HELP_GROUP_GATE: Partial<Record<HelpGroupId, HelpGate>> = { admin: "platformAdmin" };

/** Who can read an article (or a gated section inside one). Mirrors how the app gates the feature. */
export type HelpGate = "all" | "platformAdmin" | `capability:${keyof Capabilities}`;

export interface HelpArticleMeta {
  slug: string;
  group: HelpGroupId;
  gate?: HelpGate;
  /** Shown in the landing page's "Start here" list. */
  startHere?: boolean;
  related?: readonly string[];
  /** The docs/spec files this article describes: update the article when they change. */
  specs?: readonly string[];
}

export const HELP_ARTICLES = [
  { slug: "what-is-anthm", group: "start", startHere: true, related: ["first-chat", "concepts"] },
  {
    slug: "first-chat",
    group: "start",
    startHere: true,
    related: ["chat", "agents-and-models"],
    specs: ["use/home.md", "use/chat.md"],
  },
  { slug: "concepts", group: "start", startHere: true, related: ["what-is-anthm", "spaces"] },
  {
    slug: "navigation",
    group: "start",
    startHere: true,
    related: ["past-chats", "settings"],
    specs: ["shell/sidebar.md", "shell/command-palette.md"],
  },

  {
    slug: "chat",
    group: "use",
    related: ["chat-files", "agents-and-models", "sharing"],
    specs: ["use/chat.md", "use/chat-actions.md"],
  },
  { slug: "chat-files", group: "use", related: ["chat", "knowledge"], specs: ["use/chat-attachments.md"] },
  { slug: "agents-and-models", group: "use", related: ["chat", "build-agents"], specs: ["use/chat.md", "use/home.md"] },
  { slug: "sharing", group: "use", related: ["chat", "space-members"], specs: ["use/chat-sharing.md"] },
  { slug: "past-chats", group: "use", related: ["navigation", "chat"], specs: ["use/chat-history.md"] },
  {
    slug: "notifications",
    group: "use",
    related: ["sharing", "workflows", "settings"],
    specs: ["shell/notifications.md", "settings/notifications.md"],
  },
  { slug: "feedback", group: "use", related: ["navigation", "faq-troubleshooting"], specs: ["shell/feedback.md"] },

  {
    slug: "spaces",
    group: "spaces",
    related: ["space-members", "knowledge", "space-settings"],
    specs: ["use/spaces.md", "use/space-detail.md"],
  },
  { slug: "space-members", group: "spaces", related: ["spaces", "space-settings"], specs: ["use/space-sharing.md"] },
  {
    slug: "knowledge",
    group: "spaces",
    related: ["spaces", "data-sources", "chat-files"],
    specs: ["use/knowledge.md"],
  },
  {
    slug: "knowledge-inbox",
    group: "spaces",
    gate: "capability:knowledgeExtractionEnabled",
    related: ["knowledge", "settings"],
    specs: ["use/knowledge.md"],
  },
  {
    slug: "teams",
    group: "spaces",
    related: ["space-members", "sharing", "projects-and-teams"],
    specs: ["admin/teams.md"],
  },
  {
    slug: "projects-and-teams",
    group: "spaces",
    related: ["spaces", "space-members", "teams", "faq-access"],
    specs: ["use/spaces.md", "shell/sidebar.md"],
  },
  { slug: "space-settings", group: "spaces", related: ["spaces", "space-members"], specs: ["use/space-settings.md"] },

  {
    slug: "build",
    group: "build",
    related: ["build-agents", "skills", "workflows"],
    specs: ["build/build-with-ai.md"],
  },
  {
    slug: "build-agents",
    group: "build",
    related: ["skills", "data-sources", "agents-and-models"],
    specs: ["build/agents.md"],
  },
  { slug: "skills", group: "build", related: ["build-agents", "workflows"], specs: ["build/skills.md"] },
  {
    slug: "connectors",
    group: "build",
    related: ["connect-github", "connect-linear", "connect-slack", "skills"],
    specs: ["build/connectors.md"],
  },
  { slug: "connect-github", group: "build", related: ["connectors", "connect-linear"], specs: ["build/connectors.md"] },
  { slug: "connect-linear", group: "build", related: ["connectors", "connect-github"], specs: ["build/connectors.md"] },
  { slug: "connect-slack", group: "build", related: ["connectors", "settings"], specs: ["build/connectors.md"] },
  { slug: "connect-hubspot", group: "build", related: ["connect-slack", "settings"], specs: ["build/connectors.md"] },
  { slug: "connect-zoominfo", group: "build", related: ["connectors", "settings"], specs: ["build/connectors.md"] },
  { slug: "data-sources", group: "build", related: ["build-agents", "knowledge"], specs: ["build/data-sources.md"] },
  { slug: "workflows", group: "build", related: ["build-agents", "skills"], specs: ["build/workflows.md"] },
  {
    slug: "brands",
    group: "build",
    related: ["apps", "build-agents"],
    specs: ["build/brands.md", "use/chat.md", "use/space-settings.md", "build/agents.md"],
  },
  { slug: "apps", group: "build", related: ["brands", "build", "connectors"], specs: ["build/micro-apps.md"] },

  {
    slug: "settings",
    group: "account",
    related: ["navigation", "chat", "knowledge-inbox", "connectors", "notifications"],
    specs: ["use/knowledge.md", "settings/connections.md", "settings/general.md"],
  },

  { slug: "admin", group: "admin", related: ["admin-users", "admin-models", "admin-usage"] },
  { slug: "admin-users", group: "admin", related: ["admin-license", "admin-usage"], specs: ["admin/users.md"] },
  { slug: "admin-models", group: "admin", related: ["admin-usage", "admin-governance"], specs: ["admin/models.md"] },
  { slug: "admin-usage", group: "admin", related: ["admin-users", "faq-limits"], specs: ["admin/usage.md"] },
  { slug: "admin-company", group: "admin", related: ["admin", "brands"], specs: ["admin/company.md"] },
  { slug: "admin-license", group: "admin", related: ["admin-users", "faq-limits"], specs: ["admin/license.md"] },
  { slug: "admin-feedback", group: "admin", related: ["admin", "feedback"], specs: ["admin/feedback.md"] },
  {
    slug: "admin-integrations",
    group: "admin",
    related: ["admin-access", "connectors"],
    specs: ["admin/integrations.md", "build/connectors.md"],
  },
  {
    slug: "admin-governance",
    group: "admin",
    gate: "capability:azureAdminEnabled",
    related: ["admin-models", "admin-content-safety"],
    specs: ["admin/governance.md"],
  },
  {
    slug: "admin-content-safety",
    group: "admin",
    related: ["admin-governance", "admin-models"],
    specs: ["admin/content-safety.md"],
  },
  {
    slug: "admin-access",
    group: "admin",
    related: ["admin", "data-sources"],
    specs: ["admin/oauth-clients.md", "admin/authorizations.md"],
  },

  { slug: "faq-access", group: "faq", related: ["space-members", "navigation"] },
  {
    slug: "faq-limits",
    group: "faq",
    related: ["admin-usage", "admin-license"],
    specs: ["shell/sidebar.md", "settings/usage.md", "use/chat.md"],
  },
  { slug: "faq-troubleshooting", group: "faq", related: ["faq-access", "faq-limits"] },
] as const satisfies readonly HelpArticleMeta[];

export type HelpSlug = (typeof HELP_ARTICLES)[number]["slug"];

/** Typed link to a help article (and optionally one of its headings), for "Learn more" links. */
export function helpHref(slug: HelpSlug, anchor?: string): string {
  return anchor ? `/help/${slug}#${anchor}` : `/help/${slug}`;
}
